import type { PremadeTemplate } from "@/lib/premade-contract-templates";

export const PEST_CONTROL_PROPOSAL_TEMPLATES = [
  "Residential Pest Control",
  "Commercial Pest Management",
  "Termite Treatment & Prevention",
  "Mosquito & Outdoor Pest Control",
  "Bed Bug Treatment Program",
];

export const PEST_CONTROL_CONTRACT_TEMPLATES: PremadeTemplate[] = [
  {
    name: "Recurring Pest Control Agreement",
    description: "Ongoing pest management service contract with scheduled treatments, guaranteed callbacks, and renewal terms.",
    content: `RECURRING PEST CONTROL SERVICE AGREEMENT

This Pest Control Service Agreement ("Agreement") is entered into as of {{date}} by and between:

PEST CONTROL PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
License #: ________________
(hereinafter referred to as "Provider")

PROPERTY OWNER / MANAGER:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. SERVICE DESCRIPTION

Property Address: {{project_address}}
Service Type: {{project_description}}

Provider agrees to furnish all labor, chemicals, materials, and equipment necessary to perform recurring pest management services at the above property in accordance with this Agreement.

──────────────────────────────────────────────────

2. SCOPE OF SERVICES

{{scope_of_work}}

Standard recurring services include:
• Interior inspection and treatment of kitchens, bathrooms, basements, and utility areas
• Exterior perimeter treatment including foundation spray, eave dusting, and entry point sealing
• Monitoring and maintenance of bait stations
• Web removal from eaves, windows, and entry points
• Written service report after each visit

Excluded from standard service (available as add-on):
• Termite treatment and wood-destroying insect (WDI) inspections
• Bed bug treatment
• Wildlife removal and exclusion
• Fumigation / tenting services

──────────────────────────────────────────────────

3. SERVICE SCHEDULE

{{estimated_hours}}

Provider shall perform scheduled treatments according to the agreed frequency. Client will be notified at least 24 hours prior to each scheduled service visit. Client agrees to provide access to all treatment areas.

Callback Guarantee: If pest activity is observed between scheduled visits, Provider will perform a callback treatment at no additional charge within 48 hours of Client notification.

──────────────────────────────────────────────────

4. COMPENSATION

Service Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

• Payment is due upon receipt of invoice after each service visit, or billed monthly/quarterly as agreed.
• Late payments exceeding fifteen (15) days shall accrue interest at 1.5% per month.
• Provider may suspend services if payment is more than thirty (30) days past due.

──────────────────────────────────────────────────

5. CHEMICALS & SAFETY

A. All products used are EPA-registered and applied by state-licensed technicians.
B. Safety Data Sheets (SDS) are available upon request for all chemicals used.
C. Client will be informed of any re-entry intervals or pet safety precautions prior to treatment.
D. Provider follows Integrated Pest Management (IPM) best practices to minimize chemical use.
E. Provider maintains all required federal and state pesticide applicator licenses.

──────────────────────────────────────────────────

6. INSURANCE & LIABILITY

Provider shall maintain the following minimum coverage:
• Commercial General Liability: $1,000,000 per occurrence / $2,000,000 aggregate
• Workers' Compensation: As required by state law
• Pesticide Liability / Pollution Coverage: $1,000,000

Provider shall not be liable for damage to property or personal injury resulting from Client's failure to follow preparation or safety instructions.

──────────────────────────────────────────────────

7. TERM & RENEWAL

Initial Term: {{contract_length}} {{contract_length_unit}}
Auto-Renewal: This Agreement automatically renews for successive terms of equal length unless either party provides thirty (30) days' written notice of cancellation prior to the renewal date.

──────────────────────────────────────────────────

8. TERMINATION

A. Either party may terminate with thirty (30) days' written notice.
B. Client shall pay for all services rendered through the termination date.
C. Provider may terminate immediately if Client fails to provide safe access or comply with preparation instructions.

──────────────────────────────────────────────────

9. GUARANTEE & WARRANTY

Provider guarantees pest-free conditions for covered species between scheduled service visits. If covered pest activity persists after two (2) consecutive callback treatments, Provider will credit one (1) service visit to Client's account.

──────────────────────────────────────────────────

10. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

11. SIGNATURES

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "One-Time Treatment Contract",
    description: "Single-service pest treatment agreement for targeted infestations including preparation requirements and warranty.",
    content: `ONE-TIME PEST TREATMENT CONTRACT

This Pest Treatment Contract ("Contract") is entered into as of {{date}} by and between:

PEST CONTROL PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
License #: ________________
(hereinafter referred to as "Provider")

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. SERVICE DETAILS

Property Address: {{project_address}}
Target Pest(s): {{project_description}}
Treatment Method: ________________

──────────────────────────────────────────────────

2. SCOPE OF TREATMENT

{{scope_of_work}}

Provider will perform a one-time targeted treatment including:
• Thorough inspection and pest identification
• Application of appropriate treatment products to affected areas
• Sealing and exclusion of entry points as needed
• Written inspection and treatment report
• Follow-up recommendations

──────────────────────────────────────────────────

3. CLIENT PREPARATION

Client agrees to complete the following preparations prior to treatment:
• Remove or cover all food items and pet food/water dishes
• Clear treatment areas of personal items and clutter
• Ensure all persons and pets vacate the premises for the required re-entry interval
• Provide access to all areas including closets, storage rooms, and utility areas

Failure to prepare may result in rescheduling at Client's expense.

──────────────────────────────────────────────────

4. TREATMENT TIMELINE

{{estimated_hours}}

Re-entry Interval: ________ hours after treatment completion
Follow-Up Inspection: ________ days after initial treatment (if applicable)

──────────────────────────────────────────────────

5. COMPENSATION

Treatment Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

Payment is due upon completion of service unless otherwise agreed in writing.

──────────────────────────────────────────────────

6. WARRANTY

Provider warrants this treatment for ________ days from the date of service. If target pest activity recurs during the warranty period, Provider will perform one (1) re-treatment at no additional charge, provided Client followed all preparation and post-treatment instructions.

──────────────────────────────────────────────────

7. SAFETY & COMPLIANCE

All products are EPA-registered and applied by state-licensed technicians. Safety Data Sheets available upon request.

──────────────────────────────────────────────────

8. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

9. SIGNATURES

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Commercial Pest Management Contract",
    description: "Comprehensive IPM contract for commercial properties including food service, healthcare, and multi-tenant facilities.",
    content: `COMMERCIAL PEST MANAGEMENT CONTRACT

This Commercial Pest Management Contract ("Contract") is entered into as of {{date}} by and between:

PEST MANAGEMENT PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
License #: ________________
(hereinafter referred to as "Provider")

FACILITY OWNER / MANAGER:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. FACILITY INFORMATION

Facility Address: {{project_address}}
Facility Type: {{project_description}}
Square Footage: ________________
Operating Hours: ________________

──────────────────────────────────────────────────

2. INTEGRATED PEST MANAGEMENT (IPM) PROGRAM

{{scope_of_work}}

Provider shall implement a comprehensive IPM program including:

A. INSPECTION & MONITORING:
• Comprehensive initial inspection with pest activity mapping
• Ongoing monitoring with glue boards, bait stations, and insect light traps
• Monthly pest activity reports with trend analysis
• Digital documentation and service portal access

B. TREATMENT & CONTROL:
• Targeted applications using EPA-registered products only
• Crack and crevice treatment in food preparation and storage areas
• Exterior perimeter defense with granular and liquid barriers
• Rodent exclusion and bait station management

C. SANITATION CONSULTING:
• Quarterly sanitation audits with written recommendations
• Staff training on pest prevention and reporting
• Identification and documentation of conducive conditions

D. DOCUMENTATION:
• Service logbook maintained on-site
• Digital reports accessible via Provider's service portal
• Pest sighting logs and corrective action records
• Compliance documentation for health department audits

──────────────────────────────────────────────────

3. SERVICE SCHEDULE

{{estimated_hours}}

Regular Service Visits: ________________ per month
Emergency Response Time: Within 24 hours of notification
After-Hours Service: Available at standard rates

──────────────────────────────────────────────────

4. REGULATORY COMPLIANCE

Provider shall comply with all applicable federal, state, and local pest management regulations including:
• EPA pesticide use regulations
• State Department of Agriculture licensing requirements
• FDA Food Safety Modernization Act (FSMA) guidelines (if applicable)
• AIB / SQF / HACCP audit requirements (if applicable)
• OSHA workplace safety standards

──────────────────────────────────────────────────

5. COMPENSATION

Monthly Service Fee: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

• Invoiced monthly; due within thirty (30) days of invoice date
• Emergency callouts beyond the scope of this contract billed separately
• Annual rate review with thirty (30) days' notice of any adjustments

──────────────────────────────────────────────────

6. INSURANCE

Provider shall maintain:
• Commercial General Liability: $2,000,000 per occurrence / $5,000,000 aggregate
• Workers' Compensation: Statutory limits
• Pesticide Liability / Pollution Coverage: $2,000,000
• Professional Liability (E&O): $1,000,000

Certificates of insurance naming Client as additional insured provided upon request.

──────────────────────────────────────────────────

7. TERM & RENEWAL

Initial Term: {{contract_length}} {{contract_length_unit}}
Auto-Renewal: Successive one-year terms unless sixty (60) days' written notice is given.

──────────────────────────────────────────────────

8. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

9. SIGNATURES

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Termite Protection Plan",
    description: "Specialized termite treatment and ongoing protection agreement with annual inspections and damage repair warranty.",
    content: `TERMITE PROTECTION PLAN

This Termite Protection Plan ("Agreement") is entered into as of {{date}} by and between:

PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
License #: ________________
(hereinafter referred to as "Provider")

PROPERTY OWNER:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Owner")

──────────────────────────────────────────────────

1. PROPERTY

Property Address: {{project_address}}
Structure Type: {{project_description}}
Year Built: ________________
Square Footage: ________________
Foundation Type: ________________

──────────────────────────────────────────────────

2. INITIAL TREATMENT

{{scope_of_work}}

Provider shall perform a complete termite treatment including:
• WDI (Wood-Destroying Insect) inspection of entire structure
• Soil treatment with termiticide around foundation perimeter
• Treatment of accessible crawl space and basement areas
• Installation of termite monitoring stations at designated intervals
• Drill-and-treat of concrete slabs and voids as needed
• Written inspection report with findings and treatment documentation

──────────────────────────────────────────────────

3. ONGOING PROTECTION

Following initial treatment, Provider shall provide:
• Annual termite inspection of the entire property
• Monitoring station checks and maintenance (quarterly)
• Re-treatment of any areas showing new termite activity at no additional charge
• Written annual inspection report

──────────────────────────────────────────────────

4. REPAIR WARRANTY

Provider warrants that if termite damage occurs to treated areas during the warranty period, Provider will:
☐ Retreat affected areas at no charge
☐ Repair structural damage caused by covered termite species up to $________ (Repair & Retreat Plan)

This warranty covers subterranean termites. Drywood termites, carpenter ants, and other WDI are excluded unless specifically added.

──────────────────────────────────────────────────

5. COMPENSATION

Initial Treatment: \${{estimated_cost}}
Annual Renewal Fee: $________________
Payment Terms: {{payment_terms}}

{{estimated_hours}}

──────────────────────────────────────────────────

6. TERM & RENEWAL

Initial Term: {{contract_length}} {{contract_length_unit}}
Renewal: Annual renewal upon payment of renewal fee. Coverage lapses if renewal is not paid within thirty (30) days of the anniversary date.

──────────────────────────────────────────────────

7. OWNER RESPONSIBILITIES

Owner agrees to:
• Maintain proper drainage away from the foundation
• Report any signs of termite activity or moisture damage promptly
• Provide access for annual inspections and monitoring station checks
• Avoid disturbing treated soil or monitoring stations
• Disclose any prior termite history or treatment to Provider

──────────────────────────────────────────────────

8. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

9. SIGNATURES

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

OWNER:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
];

export const PEST_CONTROL_TERMINOLOGY = {
  proposalLabel: "Pest Control Proposal",
  contractLabel: "Pest Control Agreement",
  staffLabel: "Service Technician",
  rateLabel: "Technician Rate",
  industryTerms: [
    "integrated pest management",
    "IPM",
    "bait station",
    "crack and crevice",
    "perimeter spray",
    "exclusion",
    "fumigation",
    "termiticide",
    "rodent control",
    "WDI inspection",
    "monitoring station",
    "re-entry interval",
    "safety data sheet",
    "callback",
    "sanitation audit",
  ],
};
